import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { IChartApi, ISeriesApi, UTCTimestamp } from 'lightweight-charts';
import styles from '../IntradaySignalTestPage.module.css';
import { IntradaySignalUpdate } from '../../types/trading';
import { SignalMode } from './signalConfig';

const MID_COLOR = '#2563eb';
const SHORT_COLOR = '#f97316';
const LONG_COLOR = '#16a34a';
const CHART_BG = '#0a0a0a';
const CHART_TEXT = 'rgba(255, 255, 255, 0.85)';
const CHART_GRID = 'rgba(255, 255, 255, 0.08)';
const CHART_BORDER = 'rgba(255, 255, 255, 0.16)';

interface SignalChartProps {
  mode: SignalMode;
  update: IntradaySignalUpdate | null;
  resetToken: number;
  shortWindow?: number;
  longWindow?: number;
}

interface LegendValues {
  mid: number | null;
  short: number | null;
  long: number | null;
}

const EMPTY_LEGEND: LegendValues = { mid: null, short: null, long: null };

const formatNumber = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '--';
  return value.toFixed(6);
};

const toEpochSeconds = (rawTimestamp?: string): number => {
  const parsedMs = rawTimestamp ? Date.parse(rawTimestamp) : NaN;
  if (!Number.isFinite(parsedMs)) return Math.floor(Date.now() / 1000);
  return Math.floor(parsedMs / 1000);
};

export const SignalChart: React.FC<SignalChartProps> = ({
  mode,
  update,
  resetToken,
  shortWindow,
  longWindow,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [legend, setLegend] = useState<LegendValues>(EMPTY_LEGEND);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const midRef = useRef<ISeriesApi<'Line'> | null>(null);
  const shortRef = useRef<ISeriesApi<'Line'> | null>(null);
  const longRef = useRef<ISeriesApi<'Line'> | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  const showMaSeries = mode === 'ma';

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;
    let windowResizeHandler: (() => void) | null = null;

    const init = async () => {
      const mount = containerRef.current;
      if (!mount) return;
      try {
        const lwc = await import('lightweight-charts');
        if (cancelled || !containerRef.current) return;
        const chart = lwc.createChart(containerRef.current, {
          layout: {
            background: { type: lwc.ColorType.Solid, color: CHART_BG },
            textColor: CHART_TEXT,
          },
          grid: {
            vertLines: { color: CHART_GRID },
            horzLines: { color: CHART_GRID },
          },
          crosshair: { mode: lwc.CrosshairMode.Normal },
          rightPriceScale: { borderColor: CHART_BORDER },
          timeScale: {
            borderColor: CHART_BORDER,
            timeVisible: true,
            secondsVisible: true,
            rightOffset: 3,
          },
          localization: { locale: 'en-US' },
        });
        const mid = chart.addSeries(lwc.LineSeries, {
          color: MID_COLOR,
          lineWidth: 2,
          title: 'Mid Price',
          priceLineVisible: false,
          lastValueVisible: true,
        });
        const short = chart.addSeries(lwc.LineSeries, {
          color: SHORT_COLOR,
          lineWidth: 2,
          title: 'MA Short',
          priceLineVisible: false,
          lastValueVisible: true,
        });
        const long = chart.addSeries(lwc.LineSeries, {
          color: LONG_COLOR,
          lineWidth: 2,
          title: 'MA Long',
          priceLineVisible: false,
          lastValueVisible: true,
        });

        const resize = () => {
          const el = containerRef.current;
          if (!el) return;
          const rect = el.getBoundingClientRect();
          chart.applyOptions({
            width: Math.max(0, Math.floor(rect.width || 900)),
            height: Math.max(0, Math.floor(rect.height || 340)),
          });
        };
        resize();
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(resize);
          resizeObserver.observe(containerRef.current);
        } else {
          windowResizeHandler = resize;
          window.addEventListener('resize', windowResizeHandler);
        }

        chartRef.current = chart;
        midRef.current = mid;
        shortRef.current = short;
        longRef.current = long;
        chart.timeScale().fitContent();
        setError(null);
      } catch {
        setError('Unable to initialize TradingView chart.');
      }
    };
    init();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      if (windowResizeHandler) window.removeEventListener('resize', windowResizeHandler);
      chartRef.current?.remove();
      chartRef.current = null;
      midRef.current = null;
      shortRef.current = null;
      longRef.current = null;
      lastTimeRef.current = null;
    };
  }, []);

  // Toggle visibility of MA series based on mode.
  useEffect(() => {
    shortRef.current?.applyOptions({ visible: showMaSeries });
    longRef.current?.applyOptions({ visible: showMaSeries });
  }, [showMaSeries]);

  // Reset chart data when resetToken changes (e.g., restart / mode switch).
  useEffect(() => {
    midRef.current?.setData([]);
    shortRef.current?.setData([]);
    longRef.current?.setData([]);
    lastTimeRef.current = null;
    setLegend(EMPTY_LEGEND);
    chartRef.current?.timeScale().fitContent();
  }, [resetToken]);

  // Stream updates into the chart.
  useEffect(() => {
    if (!update) return;
    const chart = chartRef.current;
    const midSeries = midRef.current;
    const shortSeries = shortRef.current;
    const longSeries = longRef.current;
    if (!chart || !midSeries || !shortSeries || !longSeries) return;

    const hasMid =
      typeof update.sample_price === 'number' && Number.isFinite(update.sample_price);
    const hasShort =
      showMaSeries &&
      typeof update.ma_short_curr === 'number' &&
      Number.isFinite(update.ma_short_curr);
    const hasLong =
      showMaSeries &&
      typeof update.ma_long_curr === 'number' &&
      Number.isFinite(update.ma_long_curr);

    if (!hasMid && !hasShort && !hasLong) return;

    let nextEpoch = toEpochSeconds(update.processed_tick_ts);
    const lastEpoch = lastTimeRef.current;
    if (typeof lastEpoch === 'number' && nextEpoch <= lastEpoch) {
      nextEpoch = lastEpoch + 1;
    }
    lastTimeRef.current = nextEpoch;
    const time = nextEpoch as UTCTimestamp;

    if (hasMid) midSeries.update({ time, value: update.sample_price as number });
    if (hasShort) shortSeries.update({ time, value: update.ma_short_curr as number });
    if (hasLong) longSeries.update({ time, value: update.ma_long_curr as number });

    setLegend((prev) => ({
      mid: hasMid ? (update.sample_price as number) : prev.mid,
      short: hasShort ? (update.ma_short_curr as number) : prev.short,
      long: hasLong ? (update.ma_long_curr as number) : prev.long,
    }));
    chart.timeScale().scrollToRealTime();
  }, [update, showMaSeries]);

  const headerText = useMemo(
    () =>
      showMaSeries
        ? 'Streaming mid-price, short MA, and long MA traces from intraday signal updates.'
        : 'Streaming mid-price samples (bucket close) from the cap signal test stream.',
    [showMaSeries],
  );

  return (
    <section className={styles.chartSection}>
      <div className={styles.chartSectionHeader}>
        <h2>{showMaSeries ? 'Live MA Crossover Chart' : 'Live Price Sample Chart'}</h2>
        <p>{headerText}</p>
      </div>

      <div className={styles.chartLegend} aria-label="Chart legend">
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.midTrace}`} />
          Mid Price: {formatNumber(legend.mid)}
        </span>
        {showMaSeries ? (
          <>
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.shortTrace}`} />
              MA Short ({shortWindow || '--'}): {formatNumber(legend.short)}
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.longTrace}`} />
              MA Long ({longWindow || '--'}): {formatNumber(legend.long)}
            </span>
          </>
        ) : null}
      </div>

      <div className={styles.chartMount}>
        <div ref={containerRef} className={styles.chartContainer} data-testid="intraday-ma-chart" />
        {error ? <p className={styles.chartError}>{error}</p> : null}
      </div>
    </section>
  );
};
